import type { SalesPerson } from '../../api/clients';
import { isProfitPlus2k8ReadyForAppFeatures } from '../../api/configuration';
import type {
  ProfitPlus2k8OrgConfig,
  ProfitPlus2k8ReferenceTables,
} from '../../models/configuration';
import type { ProfitPlusPedidoRecord } from '../../models/integrations/profitPlus2k8';
import { warehouseAvailableAtSub } from '../../utils/profitPlus2k8Product';
import type {
  ProfitOrderLineRef,
  ProfitPedidoSummaryLegible,
  ProfitSyncAssessment,
  ProfitWarehouseLine,
  SubAlmacenOption,
} from './types';

/** Match legacy-bridge sub_alma / reng_ped (trimmed co_sub prefix). */
const ALMACEN_TO_SUBALMA_FALLBACK: Record<string, string> = {
  '01': 'CCS',
  '02': 'VAL',
  '03': 'MAR',
};

export type {
  ProfitOrderLineRef,
  ProfitPedidoSummaryLegible,
  ProfitSyncAssessment,
  ProfitWarehouseLine,
  SubAlmacenOption,
} from './types';

/** Whether OrderForm should render Profit Plus export fields for this org. */
export function shouldShowSaleProfitPlusFields(
  config: ProfitPlus2k8OrgConfig | null | undefined
): boolean {
  if (!config?.enabled) return false;
  if (!isProfitPlus2k8ReadyForAppFeatures(config)) return false;
  if (config.syncType !== 'export_only' && config.syncType !== 'bidirectional') {
    return false;
  }
  return config.syncAcceptedSalesToProfit === true;
}

export function getSalesPersonProfitCoVen(sp: SalesPerson): string {
  const raw = (sp.profitPlus2k8?.fields as { co_ven?: string } | undefined)
    ?.co_ven;
  return String(raw ?? '').trim();
}

export function findProfitUsdMonedaCode(
  monedas: ProfitPlus2k8ReferenceTables['monedas'] | undefined | null
): string | undefined {
  if (!monedas?.length) return undefined;
  const rows: Array<{ code: string; desUp: string }> = [];
  for (const m of monedas) {
    const code = String((m as { co_mone?: string }).co_mone ?? '').trim();
    if (!code) continue;
    rows.push({
      code,
      desUp: String((m as { mone_des?: string }).mone_des ?? '')
        .trim()
        .toUpperCase(),
    });
  }
  if (!rows.length) return undefined;
  const preferredCodes = ['$', 'USD', 'US$', 'US', 'DOL', 'DLR'];
  for (const p of preferredCodes) {
    const hit = rows.find(
      r => r.code.toUpperCase() === p.toUpperCase() || r.code === p
    );
    if (hit) return hit.code;
  }
  for (const r of rows) {
    if (/\bUSD\b|DOLAR|DOLLAR|DÓLAR|DOLARES|US\$/.test(r.desUp)) {
      return r.code;
    }
  }
  return undefined;
}

export function resolveCoSubForAlmacen(
  coSucu: string,
  subAlmacenes: ProfitPlus2k8ReferenceTables['subAlmacenes'] | undefined,
  defaultSubAlmacen?: string
): string | undefined {
  const s = String(coSucu ?? '').trim();
  if (!s) return undefined;
  const rows = subAlmacenes ?? [];
  const match = rows.find(r => String(r.co_alma ?? '').trim() === s);
  if (match?.co_sub != null && String(match.co_sub).trim()) {
    return String(match.co_sub).trim();
  }
  const def = String(defaultSubAlmacen ?? '').trim();
  if (def) return def;
  return ALMACEN_TO_SUBALMA_FALLBACK[s];
}

export function trimProfitFieldValue(v: unknown): string {
  return typeof v === 'string' ? v.trim() : String(v ?? '').trim();
}

export function resolveEffectiveCoSucu(
  saleProfitFields: Partial<ProfitPlusPedidoRecord>,
  orderExportAlmacen?: string | null
): string {
  return (
    trimProfitFieldValue(saleProfitFields.co_sucu) ||
    trimProfitFieldValue(orderExportAlmacen)
  );
}

export function legibleProfitAlmacen(
  coAlma: string | undefined,
  rows: ProfitPlus2k8ReferenceTables['almacenes'] | undefined
): string {
  const c = String(coAlma ?? '').trim();
  if (!c) return '—';
  const row = rows?.find(a => String(a.co_alma ?? '').trim() === c);
  const des = String(row?.alma_des ?? '').trim();
  return des ? `${c} — ${des}` : c;
}

export function legibleProfitMoneda(
  coMone: string | undefined,
  rows: ProfitPlus2k8ReferenceTables['monedas'] | undefined
): string {
  const c = String(coMone ?? '').trim();
  if (!c) return '—';
  const row = rows?.find(
    m => String((m as { co_mone?: string }).co_mone ?? '').trim() === c
  );
  const des = String((row as { mone_des?: string } | undefined)?.mone_des ?? '').trim();
  return des ? `${c} — ${des}` : c;
}

export function legibleProfitTransportista(
  coTran: string | undefined,
  rows: ProfitPlus2k8ReferenceTables['transportistas'] | undefined
): string {
  const c = String(coTran ?? '').trim();
  if (!c) return '—';
  const row = rows?.find(t => String(t.co_tran ?? '').trim() === c);
  const des = String(row?.des_tran ?? '').trim();
  return des ? `${c} — ${des}` : c;
}

export function legibleProfitCondicion(
  coCond: string | undefined,
  rows: ProfitPlus2k8ReferenceTables['condiciones'] | undefined
): string {
  const c = String(coCond ?? '').trim();
  if (!c) return '—';
  const row = rows?.find(x => String(x.co_cond ?? '').trim() === c);
  const des = String(row?.cond_des ?? '').trim();
  const dias =
    row?.dias_cred != null && Number.isFinite(Number(row.dias_cred))
      ? ` · ${Number(row.dias_cred)} d. crédito`
      : '';
  return des ? `${c} — ${des}${dias}` : `${c}${dias}`;
}

export function legibleProfitVendedorErp(
  coVen: string | undefined,
  rows: ProfitPlus2k8ReferenceTables['vendedoresProfit'] | undefined
): string {
  const c = String(coVen ?? '').trim();
  if (!c) return '';
  const row = rows?.find(v => String(v.co_ven ?? '').trim() === c);
  const des = String(row?.ven_des ?? '').trim();
  return des ? `${c} — ${des}` : c;
}

export function hasAnySaleProfitPedidoField(
  saleProfitFields: Partial<ProfitPlusPedidoRecord>
): boolean {
  if (trimProfitFieldValue(saleProfitFields.co_sucu)) return true;
  if (trimProfitFieldValue(saleProfitFields.co_ven)) return true;
  if (trimProfitFieldValue(saleProfitFields.moneda)) return true;
  if (
    saleProfitFields.tasa != null &&
    Number.isFinite(Number(saleProfitFields.tasa))
  ) {
    return true;
  }
  if (trimProfitFieldValue(saleProfitFields.co_tran)) return true;
  if (trimProfitFieldValue(saleProfitFields.forma_pag)) return true;
  return false;
}

export function collectProfitLineCoSubIssues(
  lines: Array<Pick<ProfitOrderLineRef, 'code' | 'name' | 'selectedCoSub'>>
): string[] {
  return lines
    .filter(p => !String(p.selectedCoSub ?? '').trim())
    .map(
      p => `línea sin subalmacén (${String(p.code || '').trim() || p.name})`
    );
}

export function isMissingProfitVendedorForExport(input: {
  selectedVendorId: string;
  selectedSalesPerson?: SalesPerson | null;
}): boolean {
  if (!input.selectedVendorId) return false;
  if (input.selectedVendorId === 'admin') return false;
  const sp = input.selectedSalesPerson;
  if (!sp) return false;
  return !getSalesPersonProfitCoVen(sp);
}

export function isProfitStep1Ready(input: {
  showSaleProfitPlusFields: boolean;
  profitUsdMonedaCode?: string;
  saleProfitFields: Partial<ProfitPlusPedidoRecord>;
  orderExportAlmacen?: string | null;
  exchangeRate: number;
}): boolean {
  if (!input.showSaleProfitPlusFields) return true;
  if (!input.profitUsdMonedaCode) return false;
  const sf = input.saleProfitFields;
  const coSucu =
    trimProfitFieldValue(sf.co_sucu) ||
    trimProfitFieldValue(input.orderExportAlmacen);
  if (!coSucu) return false;
  const monedaVal =
    trimProfitFieldValue(sf.moneda) || input.profitUsdMonedaCode;
  if (monedaVal !== input.profitUsdMonedaCode) return false;
  const tasaEffective =
    sf.tasa != null && Number.isFinite(Number(sf.tasa))
      ? Number(sf.tasa)
      : Number(input.exchangeRate);
  if (!Number.isFinite(tasaEffective) || tasaEffective <= 0) return false;
  if (!trimProfitFieldValue(sf.co_tran)) return false;
  if (!trimProfitFieldValue(sf.forma_pag)) return false;
  return true;
}

export function buildSubAlmacenOptionsFromReferenceTables(
  subAlmacenes: ProfitPlus2k8ReferenceTables['subAlmacenes'] | undefined
): SubAlmacenOption[] {
  const rows = subAlmacenes ?? [];
  const seen = new Set<string>();
  const options: SubAlmacenOption[] = [];
  for (const row of rows) {
    const coSub = String(row.co_sub ?? '').trim();
    if (!coSub || seen.has(coSub)) continue;
    seen.add(coSub);
    const coAlma = String(row.co_alma ?? '').trim();
    const description = String(row.des_sub ?? '').trim();
    const label = [coSub, coAlma ? `(${coAlma})` : '', description ? `— ${description}` : '']
      .filter(Boolean)
      .join(' ');
    options.push({
      coSub,
      coAlma,
      description,
      label: label || coSub,
    });
  }
  return options;
}

export function resolveAvailableQtyAtCoSub(
  product: ProfitWarehouseLine,
  coSub?: string
): number | undefined {
  const sub = String(coSub ?? '').trim();
  if (!sub) return undefined;
  const w = warehouseAvailableAtSub(product.profitPlus2k8?.warehouseStock, sub);
  if (typeof w === 'number' && Number.isFinite(w)) return Math.max(0, w);
  return undefined;
}

export function resolveMaxAvailableQtyAcrossSubs(
  product: ProfitWarehouseLine,
  subAlmacenOptions: SubAlmacenOption[]
): number | undefined {
  if (!subAlmacenOptions.length) return undefined;
  let maxQty: number | undefined;
  for (const opt of subAlmacenOptions) {
    const avail = resolveAvailableQtyAtCoSub(product, opt.coSub);
    if (typeof avail !== 'number') continue;
    if (maxQty == null || avail > maxQty) {
      maxQty = avail;
    }
  }
  return maxQty;
}

export function pickRecommendedCoSubForLine(input: {
  product: ProfitWarehouseLine;
  targetQty: number;
  currentCoSub?: string;
  subAlmacenOptions: SubAlmacenOption[];
  coSubForSelectedAlmacen?: string;
}): string | undefined {
  const requestedQty =
    Number.isFinite(input.targetQty) && input.targetQty > 0
      ? input.targetQty
      : 0;
  const current = String(input.currentCoSub ?? '').trim();
  const optionsByCoSub = new Map(
    input.subAlmacenOptions.map(opt => [opt.coSub, opt])
  );

  if (current && !optionsByCoSub.has(current)) {
    return current;
  }
  if (current && optionsByCoSub.has(current)) {
    const currentAvail = resolveAvailableQtyAtCoSub(input.product, current);
    if (typeof currentAvail === 'number' && currentAvail >= requestedQty) {
      return current;
    }
    return current;
  }

  const anyWithEnough = input.subAlmacenOptions.find(opt => {
    const avail = resolveAvailableQtyAtCoSub(input.product, opt.coSub);
    return typeof avail === 'number' && avail >= requestedQty;
  });
  if (anyWithEnough) return anyWithEnough.coSub;

  const bestAnyPartial = input.subAlmacenOptions
    .map(opt => ({
      coSub: opt.coSub,
      avail: resolveAvailableQtyAtCoSub(input.product, opt.coSub) ?? 0,
    }))
    .filter(item => item.avail > 0)
    .sort((a, b) => b.avail - a.avail)[0];
  if (bestAnyPartial) return bestAnyPartial.coSub;

  if (current && optionsByCoSub.has(current)) return current;
  if (current) return current;
  if (input.coSubForSelectedAlmacen) return input.coSubForSelectedAlmacen;
  return input.subAlmacenOptions[0]?.coSub;
}

export function buildSelectedLineStockWarnings(input: {
  showSaleProfitPlusFields: boolean;
  subAlmacenOptions: SubAlmacenOption[];
  selectedProducts: Array<
    ProfitWarehouseLine & Pick<ProfitOrderLineRef, 'selectedQuantity' | 'selectedCoSub'>
  >;
  formatNumber: (value: number) => string;
}): Array<{ id: string; message: string }> {
  if (!input.showSaleProfitPlusFields || input.subAlmacenOptions.length === 0) {
    return [];
  }
  const optionsByCoSub = new Map(
    input.subAlmacenOptions.map(opt => [opt.coSub, opt])
  );
  return input.selectedProducts
    .map(p => {
      const requested = Number(p.selectedQuantity) || 0;
      if (requested <= 0) return null;
      const maxAvailable = resolveMaxAvailableQtyAcrossSubs(
        p,
        input.subAlmacenOptions
      );
      if (typeof maxAvailable !== 'number' || maxAvailable >= requested) {
        return null;
      }
      const selectedSub = String(p.selectedCoSub ?? '').trim();
      const selectedSubLabel = selectedSub
        ? optionsByCoSub.get(selectedSub)?.label || selectedSub
        : 'sin subalmacén';
      return {
        id: String(p.id ?? ''),
        message: `${p.code} — ${p.name}: solicitado ${input.formatNumber(
          requested
        )}, máximo ${input.formatNumber(maxAvailable)} (${selectedSubLabel}).`,
      };
    })
    .filter(Boolean) as Array<{ id: string; message: string }>;
}

export function buildProfitPedidoSummaryLegible(input: {
  referenceTables: ProfitPlus2k8ReferenceTables | null;
  saleProfitFields: Partial<ProfitPlusPedidoRecord>;
  profitUsdMonedaCode?: string;
  effectiveCoSucu: string;
  resolvedSalesPersonCoVen: string;
  selectedVendorId: string;
  selectedSalesPerson?: SalesPerson;
  adminDisplayEmail?: string | null;
  formatSalesPersonLabel: (sp: SalesPerson) => string;
}): ProfitPedidoSummaryLegible {
  const rt = input.referenceTables;
  const sf = input.saleProfitFields;
  const monedaCode =
    String(sf.moneda ?? '').trim() || input.profitUsdMonedaCode || '';
  const coVen = input.resolvedSalesPersonCoVen;
  const venRow =
    coVen &&
    rt?.vendedoresProfit?.find(v => String(v.co_ven ?? '').trim() === coVen);
  const erpVenHasDescription = Boolean(
    venRow && String(venRow.ven_des ?? '').trim()
  );
  const erpVenLine = coVen
    ? legibleProfitVendedorErp(coVen, rt?.vendedoresProfit)
    : '';

  let vendedorPrimary = '';
  if (input.selectedVendorId === 'admin') {
    const em = input.adminDisplayEmail?.trim();
    vendedorPrimary = em ? `Administrador · ${em}` : 'Administrador';
  } else if (coVen) {
    vendedorPrimary = erpVenHasDescription
      ? erpVenLine
      : `${erpVenLine} · MayorX: ${
          input.selectedSalesPerson
            ? input.formatSalesPersonLabel(input.selectedSalesPerson)
            : '—'
        }`;
  }

  return {
    sucursal: legibleProfitAlmacen(input.effectiveCoSucu, rt?.almacenes),
    moneda: legibleProfitMoneda(monedaCode, rt?.monedas),
    transportista: legibleProfitTransportista(sf.co_tran, rt?.transportistas),
    condicion: legibleProfitCondicion(sf.forma_pag, rt?.condiciones),
    vendedorPrimary,
    vendedorMayorxSubline:
      input.selectedVendorId !== 'admin' &&
      coVen &&
      erpVenHasDescription &&
      input.selectedSalesPerson
        ? input.formatSalesPersonLabel(input.selectedSalesPerson)
        : '',
  };
}

export function buildProfitSyncAssessmentMessage(
  assessment: ProfitSyncAssessment
): string {
  const parts: string[] = [];
  if (assessment.blockers.length > 0) {
    parts.push(
      `No se creará pedido en Profit por ahora: ${assessment.blockers.join('; ')}.`
    );
  }
  if (assessment.warnings.length > 0) {
    parts.push(`Avisos para Profit Plus: ${assessment.warnings.join('; ')}.`);
  }
  return parts.join('\n\n');
}
